import type {
  AlfaPeoplePerson,
  CreateDictionaryColumnRequest,
  CreateDictionaryRequest,
  Dictionary,
  DictionaryColumn,
  DictionaryEditor,
  DictionaryEntry,
  DictionaryEntryListResponse,
  DictionaryListItem,
  DictionarySyncApplyRequest,
  DictionarySyncConfig,
  DictionarySyncPreview,
  DictionarySyncRun,
  DictionarySyncRunDetail,
  DictionarySyncState,
  DictionaryViewer,
  DirectoryUser,
  DirectoryUsersResponse,
  UpdateDictionaryColumnRequest,
  UpdateDictionaryRequest,
  UpsertDictionaryEntryRequest,
} from '@lens/shared';
import { apiRequest } from '../../shared/api/client.ts';

/**
 * Список справочников.
 *
 * `scope: 'tab'` включает фильтр видимости для вкладки «Справочники»:
 * обычный пользователь получит только доступные ему словари (создатель /
 * владелец / редактор / наблюдатель), platform_admin — все. Без scope
 * возвращаются все справочники — это нужно для настройки reference-полей
 * (picker целевого справочника), чтобы ограничение не затронуло карточки и
 * представления.
 */
export async function listDictionaries(scope?: 'tab'): Promise<DictionaryListItem[]> {
  const suffix = scope === 'tab' ? '?scope=tab' : '';
  return apiRequest<DictionaryListItem[]>(`/api/dictionaries${suffix}`, { method: 'GET' });
}

export async function getDictionary(id: string): Promise<Dictionary> {
  return apiRequest<Dictionary>(`/api/dictionaries/${id}`, { method: 'GET' });
}

export async function createDictionary(data: CreateDictionaryRequest): Promise<Dictionary> {
  return apiRequest<Dictionary>('/api/dictionaries', { method: 'POST', body: data });
}

export async function updateDictionary(
  id: string,
  data: UpdateDictionaryRequest,
): Promise<Dictionary> {
  return apiRequest<Dictionary>(`/api/dictionaries/${id}`, {
    method: 'PATCH',
    body: data,
  });
}

export async function createColumn(
  dictId: string,
  data: CreateDictionaryColumnRequest,
): Promise<DictionaryColumn> {
  return apiRequest<DictionaryColumn>(`/api/dictionaries/${dictId}/columns`, {
    method: 'POST',
    body: data,
  });
}

export async function updateColumn(
  dictId: string,
  colId: string,
  data: UpdateDictionaryColumnRequest,
): Promise<DictionaryColumn> {
  return apiRequest<DictionaryColumn>(`/api/dictionaries/${dictId}/columns/${colId}`, {
    method: 'PATCH',
    body: data,
  });
}

export async function deleteColumn(dictId: string, colId: string): Promise<void> {
  await apiRequest<void>(`/api/dictionaries/${dictId}/columns/${colId}`, {
    method: 'DELETE',
  });
}

export async function listEntries(
  dictId: string,
  params?: { cursor?: string; limit?: number; search?: string },
): Promise<DictionaryEntryListResponse> {
  const searchParams = new URLSearchParams();
  if (params?.cursor) {
    searchParams.set('cursor', params.cursor);
  }
  if (params?.limit !== undefined) {
    searchParams.set('limit', String(params.limit));
  }
  if (params?.search && params.search.trim() !== '') {
    searchParams.set('search', params.search.trim());
  }

  const query = searchParams.toString();
  const suffix = query ? `?${query}` : '';

  return apiRequest<DictionaryEntryListResponse>(`/api/dictionaries/${dictId}/entries${suffix}`, {
    method: 'GET',
  });
}

export async function createEntry(
  dictId: string,
  data: UpsertDictionaryEntryRequest,
): Promise<DictionaryEntry> {
  return apiRequest<DictionaryEntry>(`/api/dictionaries/${dictId}/entries`, {
    method: 'POST',
    body: data,
  });
}

export async function updateEntry(
  dictId: string,
  entryId: string,
  data: UpsertDictionaryEntryRequest,
): Promise<DictionaryEntry> {
  return apiRequest<DictionaryEntry>(`/api/dictionaries/${dictId}/entries/${entryId}`, {
    method: 'PUT',
    body: data,
  });
}

export async function patchEntry(
  dictId: string,
  entryId: string,
  data: UpsertDictionaryEntryRequest,
): Promise<DictionaryEntry> {
  return apiRequest<DictionaryEntry>(`/api/dictionaries/${dictId}/entries/${entryId}`, {
    method: 'PATCH',
    body: data,
  });
}

export async function deleteEntry(dictId: string, entryId: string): Promise<void> {
  await apiRequest<void>(`/api/dictionaries/${dictId}/entries/${entryId}`, {
    method: 'DELETE',
  });
}

export async function deleteDictionary(id: string): Promise<void> {
  await apiRequest<void>(`/api/dictionaries/${id}`, { method: 'DELETE' });
}

// ─── Права и доступ ─────────────────────────────────────────────────────

export async function listEditors(dictId: string): Promise<DictionaryEditor[]> {
  return apiRequest<DictionaryEditor[]>(`/api/dictionaries/${dictId}/editors`, {
    method: 'GET',
  });
}

export async function addEditor(dictId: string, userId: string): Promise<DictionaryEditor> {
  return apiRequest<DictionaryEditor>(`/api/dictionaries/${dictId}/editors`, {
    method: 'POST',
    body: { userId },
  });
}

export async function removeEditor(dictId: string, userId: string): Promise<void> {
  await apiRequest<void>(`/api/dictionaries/${dictId}/editors/${userId}`, {
    method: 'DELETE',
  });
}

export async function transferOwner(dictId: string, userId: string): Promise<Dictionary> {
  return apiRequest<Dictionary>(`/api/dictionaries/${dictId}/owner`, {
    method: 'PATCH',
    body: { userId },
  });
}

// ─── Наблюдатели (доступ «только просмотр» во вкладке) ──────────────────────

export async function listViewers(dictId: string): Promise<DictionaryViewer[]> {
  return apiRequest<DictionaryViewer[]>(`/api/dictionaries/${dictId}/viewers`, {
    method: 'GET',
  });
}

export async function addViewer(dictId: string, userId: string): Promise<DictionaryViewer> {
  return apiRequest<DictionaryViewer>(`/api/dictionaries/${dictId}/viewers`, {
    method: 'POST',
    body: { userId },
  });
}

export async function removeViewer(dictId: string, userId: string): Promise<void> {
  await apiRequest<void>(`/api/dictionaries/${dictId}/viewers/${userId}`, {
    method: 'DELETE',
  });
}

/** Лёгкий справочник пользователей для пикеров (`GET /api/users`). */
export async function listUsers(): Promise<DirectoryUser[]> {
  const res = await apiRequest<DirectoryUsersResponse>('/api/users', { method: 'GET' });
  return res.users;
}

// ─── Обновление из Alfa People ────────────────────────────────────────────

export async function getDictionarySync(dictId: string): Promise<DictionarySyncState> {
  return apiRequest<DictionarySyncState>(`/api/dictionaries/${dictId}/sync`, { method: 'GET' });
}

export async function saveDictionarySyncConfig(
  dictId: string,
  config: DictionarySyncConfig,
): Promise<DictionarySyncConfig> {
  return apiRequest<DictionarySyncConfig>(`/api/dictionaries/${dictId}/sync/config`, {
    method: 'PUT',
    body: config,
  });
}

export async function previewDictionarySync(
  dictId: string,
  people: AlfaPeoplePerson[],
): Promise<DictionarySyncPreview> {
  return apiRequest<DictionarySyncPreview>(`/api/dictionaries/${dictId}/sync/preview`, {
    method: 'POST',
    body: { people },
  });
}

export async function applyDictionarySync(
  dictId: string,
  body: DictionarySyncApplyRequest,
): Promise<DictionarySyncRun> {
  return apiRequest<DictionarySyncRun>(`/api/dictionaries/${dictId}/sync/apply`, {
    method: 'POST',
    body,
  });
}

export async function getDictionarySyncRun(dictId: string, runId: string): Promise<DictionarySyncRunDetail> {
  return apiRequest<DictionarySyncRunDetail>(`/api/dictionaries/${dictId}/sync/runs/${runId}`, {
    method: 'GET',
  });
}
